import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      console.log('🔄 Attempting login with:', credentials.email);

      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        email: credentials.email,
        password: credentials.password
      });

      console.log('✅ Login response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200 && data && data.length > 0) {
        const userData = data[0];
        const token = userData.token;

        if (!token) {
          throw new Error('No token received from server');
        }

        localStorage.setItem('access_token', token);
        if (userData.refresh_token) {
          localStorage.setItem('refresh_token', userData.refresh_token);
        }

        return {
          success: true,
          message: message || 'Login Successful',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Login failed',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Login error:', error);

      let errorMessage = 'Login failed. Please try again.';
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Login failed';

        if (serverData.errors) {
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      console.log('🔄 Attempting registration for:', userData.email);

      const response = await apiClient.post(API_ENDPOINTS.REGISTER, userData);
      console.log('✅ Registration response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Registration successful. Please verify your email.',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Registration failed',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Registration error:', error);

      let errorMessage = 'Registration failed';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Registration failed';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const registerWithOrganization = createAsyncThunk(
  'auth/registerWithOrganization',
  async (userData, { rejectWithValue }) => {
    try {
      console.log('🔄 Attempting registration for:', userData.email);

      const response = await apiClient.post(API_ENDPOINTS.REGISTER_WITH_ORGANIZATION, userData);
      console.log('✅ Registration response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Registration successful. Please verify your email.',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Registration failed',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Registration error:', error);

      let errorMessage = 'Registration failed';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Registration failed';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await apiClient.post(API_ENDPOINTS.LOGOUT);
        console.log('✅ Logout response:', response.data);

        const { message } = response.data;
        return {
          success: true,
          message: message || 'Logged out successfully'
        };
      }
      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      console.error('❌ Logout error:', error);

      let errorMessage = 'Logout failed';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      });
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('remembered_email');
    }
  }
);

export const logoutAllDevices = createAsyncThunk(
  'auth/logoutAllDevices',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Logging out from all devices...');

      const response = await apiClient.post(API_ENDPOINTS.LOGOUT_ALL_DEVICES);
      console.log('✅ Logout all devices response:', response.data);

      const { message } = response.data;

      return {
        success: true,
        message: message || 'Logged out from all devices successfully'
      };
    } catch (error) {
      console.error('❌ Logout all devices error:', error);

      let errorMessage = 'Logout from all devices failed';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status
      });
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('remembered_email');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Fetching current user...');

      const response = await apiClient.get(API_ENDPOINTS.CURRENT_USER);
      console.log('✅ Current user response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200 && data) {
        const userData = data;

        return {
          success: true,
          message: message || 'User profile retrieved',
          user: {
            id: userData.id,
            uuid: userData.uuid,
            name: userData.name,
            email: userData.email,
            phone: userData.phone,
            role: userData.role,
            organization: userData.organization,
            department: userData.department,
            position: userData.position,
            permissions: userData.permissions || [],
            status: userData.status,
            joined: userData.joined,
            last_active: userData.last_active,
            login_count: userData.login_count,
            two_factor_enabled: userData.two_factor_enabled,
            created_by: userData.created_by,
            email_verified_at: userData.email_verified_at,
            is_email_verified: userData.is_email_verified || false
          }
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to fetch user data',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Get current user error:', error);

      let errorMessage = 'Failed to fetch user';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to fetch user';

        if (statusCode === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Updating profile...');

      const response = await apiClient.post(API_ENDPOINTS.UPDATE_PROFILE, profileData);
      console.log('✅ Update profile response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200 && data) {
        return {
          success: true,
          message: message || 'Profile updated successfully',
          user: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to update profile',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Update profile error:', error);

      let errorMessage = 'Failed to update profile';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to update profile';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ current_password, new_password, new_password_confirmation }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Changing password...');

      const response = await apiClient.post(API_ENDPOINTS.CHANGE_PASSWORD, {
        current_password,
        new_password,
        new_password_confirmation
      });
      console.log('✅ Change password response:', response.data);

      const { status, message } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Password changed successfully'
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to change password',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Change password error:', error);

      let errorMessage = 'Failed to change password';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to change password';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const toggleTwoFactor = createAsyncThunk(
  'auth/toggleTwoFactor',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Toggling two-factor authentication...');

      const response = await apiClient.post(API_ENDPOINTS.TOGGLE_TWO_FACTOR);
      console.log('✅ Toggle 2FA response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Two-factor authentication toggled',
          enabled: data?.enabled || false
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to toggle two-factor authentication',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Toggle 2FA error:', error);

      let errorMessage = 'Failed to toggle two-factor authentication';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to toggle two-factor authentication';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const switchOrganization = createAsyncThunk(
  'auth/switchOrganization',
  async (organizationId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Switching organization...');

      const response = await apiClient.post(API_ENDPOINTS.SWITCH_ORGANIZATION, {
        organization_id: organizationId
      });
      console.log('✅ Switch organization response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Organization switched successfully',
          organization: data?.organization,
          organization_id: data?.organization_id
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to switch organization',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Switch organization error:', error);

      let errorMessage = 'Failed to switch organization';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to switch organization';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      console.log('🔄 Sending password reset request for:', email);

      const response = await apiClient.post(API_ENDPOINTS.FORGOT_PASSWORD, { email });
      console.log('✅ Forgot password response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Password reset link sent successfully',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to send reset link',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Forgot password error:', error);

      let errorMessage = 'Failed to send reset link';
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to send reset link';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email, token, password, password_confirmation }, { rejectWithValue }) => {
    try {
      console.log('🔄 Resetting password for:', email);

      const response = await apiClient.post(API_ENDPOINTS.RESET_PASSWORD, {
        email,
        token,
        password,
        password_confirmation
      });

      console.log('✅ Reset password response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Password reset successful',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to reset password',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Reset password error:', error);

      let errorMessage = 'Failed to reset password';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to reset password';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const verifyResetToken = createAsyncThunk(
  'auth/verifyResetToken',
  async ({ email, token }, { rejectWithValue }) => {
    try {
      console.log('🔄 Verifying reset token for:', email);

      const response = await apiClient.post(API_ENDPOINTS.VERIFY_RESET_TOKEN, {
        email,
        token
      });

      console.log('✅ Verify reset token response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Token verified successfully',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to verify reset token',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Verify reset token error:', error);

      let errorMessage = 'Failed to verify reset token';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to verify reset token';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

// =============================================
// EMAIL VERIFICATION ACTIONS
// =============================================

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async ({ token, email }, { rejectWithValue }) => {
    try {
      console.log('🔄 Verifying email for:', email);

      const response = await apiClient.post(API_ENDPOINTS.VERIFY_EMAIL, {
        email,
        token
      });

      console.log('✅ Verify email response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Email verified successfully',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to verify email',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Verify email error:', error);

      let errorMessage = 'Failed to verify email';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to verify email';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const resendVerification = createAsyncThunk(
  'auth/resendVerification',
  async ({ email }, { rejectWithValue }) => {
    try {
      console.log('🔄 Resending verification email for:', email);

      const response = await apiClient.post(API_ENDPOINTS.RESEND_VERIFICATION, {
        email
      });

      console.log('✅ Resend verification response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Verification email sent successfully',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to send verification email',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Resend verification error:', error);

      let errorMessage = 'Failed to send verification email';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to send verification email';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const checkVerificationStatus = createAsyncThunk(
  'auth/checkVerificationStatus',
  async ({ email }, { rejectWithValue }) => {
    try {
      console.log('🔄 Checking verification status for:', email);

      const response = await apiClient.post(API_ENDPOINTS.CHECK_VERIFICATION_STATUS, {
        email
      });

      console.log('✅ Check verification status response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Verification status retrieved',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to check verification status',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Check verification status error:', error);

      let errorMessage = 'Failed to check verification status';

      if (error.response) {
        errorMessage = error.response.data.message || 'Failed to check verification status';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage
      });
    }
  }
);

// =============================================
// TOKEN REFRESH
// =============================================

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Refreshing token...');

      try {
        const refreshTokenStr = localStorage.getItem('refresh_token');
        if (!refreshTokenStr) {
          throw new Error('No refresh token available');
        }

        const response = await apiClient.post(API_ENDPOINTS.REFRESH_TOKEN, {
          refresh_token: refreshTokenStr
        });

        const { status, message, data } = response.data;

        if (status === 200 && data && data.token) {
          const newToken = data.token;
          localStorage.setItem('access_token', newToken);
          return {
            success: true,
            token: newToken,
            message: message || 'Token refreshed successfully'
          };
        } else {
          return rejectWithValue({
            message: message || 'Token refresh failed',
            status: status,
            apiResponse: response.data
          });
        }
      } catch (refreshError) {
        console.log('ℹ️ Token refresh failed, using existing token');
        return {
          success: true,
          token: token,
          message: 'Using existing token'
        };
      }

    } catch (error) {
      console.error('❌ Token refresh error:', error);

      let errorMessage = 'Token refresh failed';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Token refresh failed';
      }

      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

// =============================================
// ORGANIZATIONS & ROLES
// =============================================

export const getOrganizations = createAsyncThunk(
  'auth/getOrganizations',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching organizations...');

      const response = await apiClient.get(API_ENDPOINTS.GET_ORGANIZATIONS);
      console.log('✅ Organizations response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Organizations retrieved successfully',
          organizations: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to fetch organizations',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Get organizations error:', error);

      let errorMessage = 'Failed to fetch organizations';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to fetch organizations';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const getRoles = createAsyncThunk(
  'auth/getRoles',
  async (_, { rejectWithValue }) => {
    try {
      console.log('🔄 Fetching roles...');

      const response = await apiClient.get(API_ENDPOINTS.GET_ROLES);
      console.log('✅ Roles response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Roles retrieved successfully',
          roles: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to fetch roles',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Get roles error:', error);

      let errorMessage = 'Failed to fetch roles';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to fetch roles';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

// =============================================
// NOTIFICATION PREFERENCES
// =============================================

export const getNotificationPreferences = createAsyncThunk(
  'auth/getNotificationPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Fetching notification preferences...');

      const response = await apiClient.get(API_ENDPOINTS.GET_NOTIFICATION_PREFERENCES);
      console.log('✅ Notification preferences response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Notification preferences retrieved',
          preferences: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to fetch notification preferences',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Get notification preferences error:', error);

      let errorMessage = 'Failed to fetch notification preferences';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to fetch notification preferences';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  'auth/updateNotificationPreferences',
  async (preferences, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Updating notification preferences...');

      const response = await apiClient.post(API_ENDPOINTS.UPDATE_NOTIFICATION_PREFERENCES, preferences);
      console.log('✅ Update notification preferences response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Notification preferences updated successfully',
          preferences: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to update notification preferences',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Update notification preferences error:', error);

      let errorMessage = 'Failed to update notification preferences';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to update notification preferences';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const toggleNotificationPreference = createAsyncThunk(
  'auth/toggleNotificationPreference',
  async (type, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log(`🔄 Toggling notification preference: ${type}...`);

      const response = await apiClient.post(
        `${API_ENDPOINTS.TOGGLE_NOTIFICATION_PREFERENCE}${type}`
      );
      console.log('✅ Toggle notification preference response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Notification preference toggled successfully',
          preference: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to toggle notification preference',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Toggle notification preference error:', error);

      let errorMessage = 'Failed to toggle notification preference';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to toggle notification preference';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const resetNotificationPreferences = createAsyncThunk(
  'auth/resetNotificationPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No token available');
      }

      console.log('🔄 Resetting notification preferences...');

      const response = await apiClient.post(API_ENDPOINTS.RESET_NOTIFICATION_PREFERENCES);
      console.log('✅ Reset notification preferences response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Notification preferences reset successfully',
          preferences: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to reset notification preferences',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Reset notification preferences error:', error);

      let errorMessage = 'Failed to reset notification preferences';
      let statusCode = 500;

      if (error.response) {
        statusCode = error.response.status;
        const serverData = error.response.data;
        errorMessage = serverData.message || serverData.error || 'Failed to reset notification preferences';
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      console.log('🔄 Verifying OTP for phone:', phone);

      const response = await apiClient.post(API_ENDPOINTS.VERIFY_OTP, {
        phone,
        otp
      });

      console.log('✅ Verify OTP response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'Phone verified successfully',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to verify OTP',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Verify OTP error:', error);

      let errorMessage = 'Failed to verify OTP';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to verify OTP';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

export const resendOtp = createAsyncThunk(
  'auth/resendOtp',
  async ({ phone }, { rejectWithValue }) => {
    try {
      console.log('🔄 Resending OTP for phone:', phone);

      const response = await apiClient.post(API_ENDPOINTS.RESEND_OTP, {
        phone
      });

      console.log('✅ Resend OTP response:', response.data);

      const { status, message, data } = response.data;

      if (status === 200) {
        return {
          success: true,
          message: message || 'OTP sent successfully',
          data: data
        };
      } else {
        return rejectWithValue({
          message: message || 'Failed to send OTP',
          status: status,
          apiResponse: response.data
        });
      }
    } catch (error) {
      console.error('❌ Resend OTP error:', error);

      let errorMessage = 'Failed to send OTP';
      let errorData = {};
      let statusCode = 500;

      if (error.response) {
        const serverData = error.response.data;
        statusCode = error.response.status;
        errorMessage = serverData.message || serverData.error || 'Failed to send OTP';

        if (serverData.errors) {
          errorData = serverData.errors;
          const errorMessages = Object.values(serverData.errors).flat().join(', ');
          errorMessage = errorMessages || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = error.message || 'An unexpected error occurred';
      }

      return rejectWithValue({
        message: errorMessage,
        errors: errorData,
        status: statusCode,
        apiResponse: error.response?.data
      });
    }
  }
);

// =============================================
// SLICE
// =============================================

const initialState = {
  user: null,
  token: localStorage.getItem('access_token') || null,
  refreshToken: localStorage.getItem('refresh_token') || null,
  isAuthenticated: !!localStorage.getItem('access_token'),
  isLoading: false,
  error: null,
  successMessage: null,
  apiMessage: null,
  registrationStatus: null,
  passwordResetStatus: null,
  permissions: [],
  roles: [],
  twoFactorRequired: false,
  twoFactorVerified: false,
  organization: null,
  loginCount: 0,
  notificationPreferences: null,
  // Email verification states
  // requiresVerification: false,
  // verificationStatus: null,
  // Phone verification states
  requiresPhoneVerification: false,
  phoneVerificationStatus: null,
  // Organizations and roles
  organizations: [],
  availableRoles: [],
  isLoadingOrganizations: false,
  isLoadingRoles: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearApiMessage: (state) => {
      state.apiMessage = null;
    },
    setTwoFactorRequired: (state, action) => {
      state.twoFactorRequired = action.payload;
    },
    setTwoFactorVerified: (state, action) => {
      state.twoFactorVerified = action.payload;
    },
    setRequiresVerification: (state, action) => {
      state.requiresVerification = action.payload;
    },
    clearAuthState: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      state.successMessage = null;
      state.apiMessage = null;
      state.permissions = [];
      state.roles = [];
      state.twoFactorRequired = false;
      state.twoFactorVerified = false;
      state.organization = null;
      state.loginCount = 0;
      state.notificationPreferences = null;
      state.requiresVerification = false;
      state.verificationStatus = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('remembered_email');
    },
    resetAuthState: (state) => {
      state.error = null;
      state.successMessage = null;
      state.apiMessage = null;
      state.registrationStatus = null;
      state.passwordResetStatus = null;
      state.verificationStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== LOGIN =====
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
        state.permissions = action.payload.user?.permissions || [];
        if (action.payload.user?.role) {
          state.roles = [action.payload.user.role.toLowerCase().replace(/\s+/g, '_')];
        }
        if (action.payload.user?.organization) {
          state.organization = action.payload.user.organization;
        }
        if (action.payload.user?.login_count) {
          state.loginCount = action.payload.user.login_count;
        }
        // Check if email is verified
        if (action.payload.user?.is_email_verified !== undefined) {
          state.requiresVerification = !action.payload.user.is_email_verified;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload?.message || 'Login failed';
        state.apiMessage = action.payload?.message || 'Login failed';
        state.successMessage = null;
        // Check if login failed because of unverified email
        if (action.payload?.requires_verification) {
          state.requiresVerification = true;
        }
      })

      // ===== REGISTER =====
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
        state.registrationStatus = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.registrationStatus = 'success';
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
        state.requiresVerification = true;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.registrationStatus = 'failed';
        state.error = action.payload?.message || 'Registration failed';
        state.apiMessage = action.payload?.message || 'Registration failed';
        state.successMessage = null;
      })

      // ===== REGISTER WITH ORGANIZATION =====
      .addCase(registerWithOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
        state.registrationStatus = null;
      })
      .addCase(registerWithOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.registrationStatus = 'success';
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
        state.requiresVerification = true;
      })
      .addCase(registerWithOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.registrationStatus = 'failed';
        state.error = action.payload?.message || 'Registration failed';
        state.apiMessage = action.payload?.message || 'Registration failed';
        state.successMessage = null;
      })

      // ===== LOGOUT =====
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
        state.apiMessage = null;
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.successMessage = action.payload?.message || 'Logged out successfully';
        state.apiMessage = action.payload?.message || 'Logged out successfully';
        state.permissions = [];
        state.roles = [];
        state.twoFactorRequired = false;
        state.twoFactorVerified = false;
        state.organization = null;
        state.loginCount = 0;
        state.notificationPreferences = null;
        state.requiresVerification = false;
        state.verificationStatus = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = action.payload?.message || 'Logout failed';
        state.apiMessage = action.payload?.message || 'Logout failed';
        state.permissions = [];
        state.roles = [];
        state.organization = null;
        state.loginCount = 0;
        state.notificationPreferences = null;
        state.requiresVerification = false;
        state.verificationStatus = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('remembered_email');
      })

      // ===== LOGOUT ALL DEVICES =====
      .addCase(logoutAllDevices.pending, (state) => {
        state.isLoading = true;
        state.apiMessage = null;
      })
      .addCase(logoutAllDevices.fulfilled, (state, action) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = null;
        state.successMessage = action.payload?.message || 'Logged out from all devices';
        state.apiMessage = action.payload?.message || 'Logged out from all devices';
        state.permissions = [];
        state.roles = [];
        state.twoFactorRequired = false;
        state.twoFactorVerified = false;
        state.organization = null;
        state.loginCount = 0;
        state.notificationPreferences = null;
        state.requiresVerification = false;
        state.verificationStatus = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('remembered_email');
      })
      .addCase(logoutAllDevices.rejected, (state, action) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.error = action.payload?.message || 'Logout from all devices failed';
        state.apiMessage = action.payload?.message || 'Logout from all devices failed';
        state.permissions = [];
        state.roles = [];
        state.organization = null;
        state.loginCount = 0;
        state.notificationPreferences = null;
        state.requiresVerification = false;
        state.verificationStatus = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('remembered_email');
      })

      // ===== GET CURRENT USER =====
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.isAuthenticated = true;
        state.permissions = action.payload.user?.permissions || [];
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
        if (action.payload.user?.role) {
          state.roles = [action.payload.user.role.toLowerCase().replace(/\s+/g, '_')];
        }
        if (action.payload.user?.organization) {
          state.organization = action.payload.user.organization;
        }
        // Check email verification status
        // if (action.payload.user?.is_email_verified !== undefined) {
        //   state.requiresVerification = !action.payload.user.is_email_verified;
        // }

        // Phone verification status
        // if (action.payload.user?.is_phone_verified !== undefined) {
        //   state.requiresPhoneVerification = !action.payload.user.is_phone_verified;
        // }

        if (action.payload.user?.is_email_verified !== undefined) {
          state.requiresPhoneVerification = !action.payload.user.is_email_verified;
        }
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch user';
        state.apiMessage = action.payload?.message || 'Failed to fetch user';
        state.successMessage = null;
        if (action.payload?.status === 401) {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          state.permissions = [];
          state.roles = [];
          state.organization = null;
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      })

      // ===== UPDATE PROFILE =====
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = { ...state.user, ...action.payload.user };
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
        if (action.payload.user?.role) {
          state.roles = [action.payload.user.role.toLowerCase().replace(/\s+/g, '_')];
        }
        if (action.payload.user?.organization) {
          state.organization = action.payload.user.organization;
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to update profile';
        state.apiMessage = action.payload?.message || 'Failed to update profile';
        state.successMessage = null;
      })

      // ===== CHANGE PASSWORD =====
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to change password';
        state.apiMessage = action.payload?.message || 'Failed to change password';
        state.successMessage = null;
      })

      // ===== TOGGLE TWO-FACTOR =====
      .addCase(toggleTwoFactor.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(toggleTwoFactor.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.two_factor_enabled = action.payload.enabled;
        }
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(toggleTwoFactor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to toggle two-factor authentication';
        state.apiMessage = action.payload?.message || 'Failed to toggle two-factor authentication';
        state.successMessage = null;
      })

      // ===== SWITCH ORGANIZATION =====
      .addCase(switchOrganization.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(switchOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.organization = action.payload.organization;
        }
        state.organization = action.payload.organization;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(switchOrganization.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to switch organization';
        state.apiMessage = action.payload?.message || 'Failed to switch organization';
        state.successMessage = null;
      })

      // ===== FORGOT PASSWORD =====
      .addCase(forgotPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
        state.passwordResetStatus = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = 'success';
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = 'failed';
        state.error = action.payload?.message || 'Failed to send reset link';
        state.apiMessage = action.payload?.message || 'Failed to send reset link';
        state.successMessage = null;
      })

      // ===== RESET PASSWORD =====
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
        state.passwordResetStatus = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = 'success';
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = 'failed';
        state.error = action.payload?.message || 'Failed to reset password';
        state.apiMessage = action.payload?.message || 'Failed to reset password';
        state.successMessage = null;
      })

      // ===== VERIFY RESET TOKEN =====
      .addCase(verifyResetToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
        state.apiMessage = null;
        state.passwordResetStatus = null;
      })
      .addCase(verifyResetToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = 'success';
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(verifyResetToken.rejected, (state, action) => {
        state.isLoading = false;
        state.passwordResetStatus = 'failed';
        state.error = action.payload?.message || 'Failed to verify reset token';
        state.apiMessage = action.payload?.message || 'Failed to verify reset token';
        state.successMessage = null;
      })

      // ===== VERIFY EMAIL =====
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
        state.verificationStatus = 'verifying';
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apiMessage = action.payload.message;
        state.successMessage = action.payload.message;
        state.requiresVerification = false;
        state.verificationStatus = 'verified';
        state.error = null;
        // Update user's email verification status
        if (state.user) {
          state.user.is_email_verified = true;
          state.user.email_verified_at = new Date().toISOString();
        }
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to verify email';
        state.apiMessage = action.payload?.message || 'Failed to verify email';
        state.verificationStatus = 'failed';
        state.successMessage = null;
        // Check if expired and new token was sent
        if (action.payload?.expired && action.payload?.new_token_sent) {
          state.apiMessage = 'Your verification link has expired. A new one has been sent to your email.';
        }
      })

      // ===== RESEND VERIFICATION =====
      .addCase(resendVerification.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(resendVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apiMessage = action.payload.message;
        state.successMessage = action.payload.message;
        state.error = null;
        state.verificationStatus = 'resent';
      })
      .addCase(resendVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to resend verification email';
        state.apiMessage = action.payload?.message || 'Failed to resend verification email';
        state.successMessage = null;
      })

      // ===== CHECK VERIFICATION STATUS =====
      .addCase(checkVerificationStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkVerificationStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.data) {
          state.requiresVerification = !action.payload.data.verified;
          if (state.user) {
            state.user.is_email_verified = action.payload.data.verified;
            state.user.email_verified_at = action.payload.data.verified_at;
          }
        }
        state.error = null;
      })
      .addCase(checkVerificationStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to check verification status';
      })

      // ===== REFRESH TOKEN =====
      .addCase(refreshToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.token = action.payload.token;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.permissions = [];
        state.roles = [];
        state.organization = null;
        state.notificationPreferences = null;
        state.requiresVerification = false;
        state.error = action.payload?.message || 'Token refresh failed';
        state.apiMessage = action.payload?.message || 'Token refresh failed';
        state.successMessage = null;
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      })

      // ===== GET ORGANIZATIONS =====
      .addCase(getOrganizations.pending, (state) => {
        state.isLoadingOrganizations = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.isLoadingOrganizations = false;
        state.organizations = action.payload.organizations;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(getOrganizations.rejected, (state, action) => {
        state.isLoadingOrganizations = false;
        state.error = action.payload?.message || 'Failed to fetch organizations';
        state.apiMessage = action.payload?.message || 'Failed to fetch organizations';
        state.organizations = [];
      })

      // ===== GET ROLES =====
      .addCase(getRoles.pending, (state) => {
        state.isLoadingRoles = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoadingRoles = false;
        state.availableRoles = action.payload.roles;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoadingRoles = false;
        state.error = action.payload?.message || 'Failed to fetch roles';
        state.apiMessage = action.payload?.message || 'Failed to fetch roles';
        state.availableRoles = [];
      })

      // ===== GET NOTIFICATION PREFERENCES =====
      .addCase(getNotificationPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(getNotificationPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notificationPreferences = action.payload.preferences;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(getNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to fetch notification preferences';
        state.apiMessage = action.payload?.message || 'Failed to fetch notification preferences';
        state.successMessage = null;
      })

      // ===== UPDATE NOTIFICATION PREFERENCES =====
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notificationPreferences = action.payload.preferences;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to update notification preferences';
        state.apiMessage = action.payload?.message || 'Failed to update notification preferences';
        state.successMessage = null;
      })

      // ===== TOGGLE NOTIFICATION PREFERENCE =====
      .addCase(toggleNotificationPreference.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(toggleNotificationPreference.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.notificationPreferences && action.payload.preference) {
          const { type, enabled } = action.payload.preference;
          state.notificationPreferences[type] = enabled;
        }
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(toggleNotificationPreference.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to toggle notification preference';
        state.apiMessage = action.payload?.message || 'Failed to toggle notification preference';
        state.successMessage = null;
      })

      // ===== RESET NOTIFICATION PREFERENCES =====
      .addCase(resetNotificationPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(resetNotificationPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notificationPreferences = action.payload.preferences;
        state.successMessage = action.payload.message;
        state.apiMessage = action.payload.message;
        state.error = null;
      })
      .addCase(resetNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to reset notification preferences';
        state.apiMessage = action.payload?.message || 'Failed to reset notification preferences';
        state.successMessage = null;
      })

      // ===== VERIFY OTP =====
      .addCase(verifyOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
        state.phoneVerificationStatus = 'verifying';
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apiMessage = action.payload.message;
        state.successMessage = action.payload.message;
        state.requiresPhoneVerification = false;
        state.phoneVerificationStatus = 'verified';
        state.error = null;
        if (state.user) {
          state.user.is_email_verified = true;
          state.user.phone_verified_at = new Date().toISOString();
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to verify OTP';
        state.apiMessage = action.payload?.message || 'Failed to verify OTP';
        state.phoneVerificationStatus = 'failed';
        state.successMessage = null;
        if (action.payload?.expired && action.payload?.new_token_sent) {
          state.apiMessage = 'Your OTP has expired. A new one has been sent to your phone.';
        }
      })

      // ===== RESEND OTP =====
      .addCase(resendOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.apiMessage = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apiMessage = action.payload.message;
        state.successMessage = action.payload.message;
        state.error = null;
        state.phoneVerificationStatus = 'resent';
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Failed to resend OTP';
        state.apiMessage = action.payload?.message || 'Failed to resend OTP';
        state.successMessage = null;
      })
  },
});

// =============================================
// SELECTORS
// =============================================

export const {
  clearError,
  clearSuccessMessage,
  clearApiMessage,
  setTwoFactorRequired,
  setTwoFactorVerified,
  setRequiresVerification,
  clearAuthState,
  resetAuthState,
} = authSlice.actions;

export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectCurrentUser = (state) => state.auth.user;
export const selectAuthToken = (state) => state.auth.token;
export const selectAuthLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthSuccess = (state) => state.auth.successMessage;
export const selectApiMessage = (state) => state.auth.apiMessage;
export const selectUserPermissions = (state) => state.auth.permissions;
export const selectUserRoles = (state) => state.auth.roles;
export const selectRegistrationStatus = (state) => state.auth.registrationStatus;
export const selectPasswordResetStatus = (state) => state.auth.passwordResetStatus;
export const selectTwoFactorRequired = (state) => state.auth.twoFactorRequired;
export const selectTwoFactorVerified = (state) => state.auth.twoFactorVerified;
export const selectCurrentOrganization = (state) => state.auth.organization;
export const selectLoginCount = (state) => state.auth.loginCount;
export const selectUserName = (state) => state.auth.user?.name || '';
export const selectUserEmail = (state) => state.auth.user?.email || '';
export const selectUserRole = (state) => state.auth.user?.role || '';
export const selectUserPermissionsList = (state) => state.auth.permissions || [];
export const selectNotificationPreferences = (state) => state.auth.notificationPreferences;
export const selectOrganizations = (state) => state.auth.organizations;
export const selectAvailableRoles = (state) => state.auth.availableRoles;
export const selectIsLoadingOrganizations = (state) => state.auth.isLoadingOrganizations;
export const selectIsLoadingRoles = (state) => state.auth.isLoadingRoles;

// Email verification selectors
export const selectRequiresVerification = (state) => state.auth.requiresVerification;
export const selectVerificationStatus = (state) => state.auth.verificationStatus;
export const selectIsEmailVerified = (state) => state.auth.user?.is_email_verified || false;

export const selectRequiresPhoneVerification = (state) => state.auth.requiresPhoneVerification;
export const selectPhoneVerificationStatus = (state) => state.auth.phoneVerificationStatus;
export const selectIsPhoneVerified = (state) => state.auth.user?.is_email_verified || false;

export default authSlice.reducer;